import { Model, Factory, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randBoolean,
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from '../server.js'
import type { SchemaAttributes } from '../../types.js'
import { getTodayDateTimeISO } from '@/utils/date/getter.js'
import {
  pageable,
  filterable,
  createErrorResponse,
  createHtmlParagraph,
} from '../utils.js'

export type ConsultationNoteData = SchemaAttributes<'ConsultationNote'>

export const model = (Model as ModelDefinition<ConsultationNoteData>).extend({
  consultation: belongsTo(),
  patient: belongsTo('patient'),
  customType: belongsTo('listItem'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
})

export const factory = Factory.extend<ConsultationNoteData>({
  text() {
    return createHtmlParagraph()
  },
  draft() {
    return randBoolean()
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  archived_at() {
    return randChanceBoolean({ chanceTrue: 0.4 })
      ? randPastDate().toISOString()
      : null
  },
  is_editable() {
    return randBoolean()
  },
  is_deletable() {
    return randBoolean()
  },
  withCreatedUser: trait({
    afterCreate(note, server) {
      const createdUser = server.create('user')
      note.update({ createdUser })
    },
  }),
  withModifiedUser: trait({
    afterCreate(note, server) {
      const modifiedUser = server.create('user')
      note.update({ modifiedUser })
    },
  }),
  withCustomType: trait({
    afterCreate(note, server) {
      const customType = server.create('listItem', 'asConsultationNoteType')
      note.update({ customType })
    },
  }),
  afterCreate(note, server) {
    if (!note.consultation) {
      const consultation = server.create('consultation')
      note.update({ consultation })
    }

    // Set patient from consultation if not already set
    if (!note.patient && note.consultation?.patients?.models?.length > 0) {
      note.update({ patient: note.consultation.patients.models[0] })
    }
  },
})

export function routes(server: MockServer) {
  server.resource('consultationNote', { path: '/consultation_notes' })

  server.get(
    '/consultations/:parent_lookup_consultation/consultation_notes',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const notes = schema.where('consultationNote', { consultationId })

      const filtered = filterable(notes, request, {
        search: (item, value) =>
          item.attrs.text?.toLowerCase().includes(value.toLowerCase()),
        consultation: (item, value) => item.attrs.consultationId === value,
        custom_type: (item, value) => item.attrs.customTypeId === value,
        general: (item) => (!item.attrs.customTypeId ? true : false),
        draft: (item) => item.attrs.draft,
        is_active: (item, value) => {
          const isActive = !item.attrs.archived_at
          return value === 'false' ? !isActive : isActive
        },
        user: (item, value) =>
          item.attrs.createdUserId === value ||
          item.attrs.modifiedUserId === value,
      })

      const { data, meta } = pageable(filtered, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.get(
    '/consultations/:parent_lookup_consultation/consultation_notes/users',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const notes = schema.where('consultationNote', { consultationId })

      const userIds = new Set<string>()

      notes.models.forEach((note) => {
        if (note.createdUser?.id) {
          userIds.add(note.createdUser.id)
        }
        if (note.modifiedUser?.id) {
          userIds.add(note.modifiedUser.id)
        }
      })

      const users = schema.where(
        'user',
        (user) => user.id && userIds.has(user.id),
      )

      return this.serialize(users)
    },
  )

  server.get('/consultation_notes/:id', function (schema, request) {
    const id = request.params.id
    const note = schema.find('consultationNote', id)

    if (!note) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation Note with id ${id} not found` }],
      })
    }

    return note
  })

  server.post(
    '/consultations/:parent_lookup_consultation/consultation_notes',
    function (schema, request) {
      const attrs = JSON.parse(request.requestBody)
      const relationships = attrs.data.relationships
      const consultationId = request.params.parent_lookup_consultation

      const note = schema.create('consultationNote', attrs.data.attributes)

      if (consultationId) {
        const consultation = schema.find('consultation', consultationId)
        note.update({ consultation })
      }

      if (relationships.custom_type.data) {
        const customType = schema.find(
          'listItem',
          relationships.custom_type.data.id,
        )
        note.update({ customType })
      }

      return note
    },
  )

  server.patch('/consultation_notes/:id/archive', function (schema, request) {
    const id = request.params.id
    const note = schema.find('consultationNote', id)

    if (!note) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation Note with id ${id} not found` }],
      })
    }
    note.update({ archived_at: getTodayDateTimeISO() })
    return note
  })

  server.patch('/consultation_notes/:id/restore', function (schema, request) {
    const id = request.params.id
    const note = schema.find('consultationNote', id)

    if (!note) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation Note with id ${id} not found` }],
      })
    }
    note.update({ archived_at: null })
    return note
  })

  server.patch('/consultation_notes/:id', async function (schema, request) {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const note = schema.find('consultationNote', id)

    if (!note) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation Note with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    await note.update(attrs.data.attributes)
    return note
  })

  server.delete('/consultation_notes/:id', function (schema, request) {
    const id = request.params.id
    const note = schema.find('consultationNote', id)

    if (!note) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation Note with id ${id} not found` }],
      })
    }

    server.db.consultationNotes.remove(note)
    return note
  })
}

export function seeds(server: MockServer) {
  server.schema.all('consultation').models.forEach((consultation) => {
    const noteCount = randNumber({ min: 1, max: 2 })
    server.createList(
      'consultationNote',
      noteCount,
      'withCreatedUser',
      'withModifiedUser',
      'withCustomType',
      {
        consultation,
      },
    )

    server.createList(
      'consultationNote',
      noteCount,
      'withCreatedUser',
      'withModifiedUser',
      {
        consultation,
      },
    )

    server.createList(
      'consultationNote',
      noteCount,
      'withCreatedUser',
      'withModifiedUser',
      {
        consultation,
        archived_at: null,
      },
    )
  })
}
